import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, Input } from '@angular/core';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { Route, RouterLinkActive, RouterLink } from '@angular/router';
import { patchState, signalState } from '@ngrx/signals';
import { explicitEffect } from 'ngxtension/explicit-effect';

import { AppRoute } from '../../../types/app-route';
import { NgClass } from '@angular/common';
import { LetDirective } from '@ngrx/component';
import { MatNavList, MatListItem } from '@angular/material/list';
import { MatTooltip } from '@angular/material/tooltip';
import { MatLine } from '@angular/material/core';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { ExperimentalTagComponent } from '../../experimental-features/experimental-tag.component';
import { SkipNavigationDirective } from '../../../directives/skip-navigation.directive';
import { ExperimentalFeatureService } from '../../experimental-features/experimental-feature.service';
import { NavigationService } from '../../../services/navigation.service';
import { AuthorizationService } from '../../../auth/authorization/services/authorization.service';
import { SpherePluginPermissionService } from '../../../auth/authorization/services/sphere-plugin-permission.service';
import { FunctionWrapperPipe } from '../../../pipes/function-wrapper.pipe';
import { AuthenticationService } from '../../../auth/authentication/authentication.service';

export type SidebarNavState = {
    app: AppRoute | null;
    appRoutes: AppRoute[];
};

@Component({
    selector: 'sphere-shell-sidebar-nav',
    templateUrl: './shell-sidebar-nav.component.html',
    styleUrl: './shell-sidebar-nav.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        NgClass,
        LetDirective,
        MatNavList,
        MatListItem,
        RouterLinkActive,
        RouterLink,
        SkipNavigationDirective,
        MatTooltip,
        MatLine,
        ExperimentalTagComponent,
        MatIconButton,
        MatIcon,
        FunctionWrapperPipe
    ]
})
export class ShellSidebarNavComponent {
    navigationService = inject(NavigationService);
    private sanitizer = inject(DomSanitizer);
    private authentication = inject(AuthenticationService);
    private experimentalFeatures = inject(ExperimentalFeatureService);
    private sphereApps = inject(SpherePluginPermissionService);

    private readonly authorization = inject(AuthorizationService);

    public collapsedSidenav = input(false);

    public readonly state = signalState<SidebarNavState>({
        app: null,
        appRoutes: []
    });

    expandableState: boolean[] = [];

    public apps = this.authorization.getAccessibleSphereApps(this.navigationService.apps);
    public currentAppData = computed(() => {
        if (this.state.app()) {
            return this.state.app()?.data;
        }
        return null;
    });
    public currentAppName = computed(() => {
        if (this.currentAppData()) {
            return this.currentAppData()?.name;
        }
        return null;
    });

    private currentApp = this.navigationService.currentApp;
    public isShell = this.navigationService.isShell;

    private readonly destroyRef = inject(DestroyRef);

    constructor() {
        patchState(this.state, { appRoutes: [] });

        explicitEffect([this.currentApp], ([currentApp]) => {
            patchState(this.state, {
                app: currentApp,
                appRoutes: currentApp ? this.navigationService.getRoutes(currentApp) : ([] as AppRoute[])
            });
        });
    }

    /**
     * Returns the list of visible routes.
     *
     */
    visibleRoutes = computed(() => {
        if (!this.state.appRoutes() || this.state.appRoutes().length === 0) {
            return [];
        }
        return this.state
            .appRoutes()
            .filter(route => !!route.data && !route.data['navHidden'])
            .filter(route => !route.data['superAdminRoute'] || this.authorization.isSuperAdmin())
            .filter(route => (route.data['alpha'] ? this.experimentalFeatures.hasAlphaFeatureAccess : true))
            .filter(route => (route.data['beta'] ? this.experimentalFeatures.hasBetaFeatureAccess : true));
    });
    getVisibleRoutes = (routes: Route[]): Route[] => {
        return (
            routes &&
            routes
                .filter(route => !!route.data && !route.data['navHidden'])
                .filter(route => !route.data?.['superAdminRoute'] || this.authorization.isSuperAdmin())
                .filter(route => (route.data?.['alpha'] ? this.experimentalFeatures.hasAlphaFeatureAccess : true))
                .filter(route => (route.data?.['beta'] ? this.experimentalFeatures.hasBetaFeatureAccess : true))
        );
    };

    getLogo = (logoUri: string): SafeStyle => {
        return this.sanitizer.bypassSecurityTrustStyle(`url("${logoUri}")`);
    };

    toggle(event: Event, index: number) {
        if (!this.collapsedSidenav) {
            event.preventDefault();
            event.stopPropagation();
        }
        this.expandableState[index] = !this.expandableState[index];
        console.debug(`[DEBUG][SIDE-NAV] Expand state for INDEX [${index}]: ${this.expandableState[index]}`);
    }

    hasAccess(app: AppRoute) {
        return this.sphereApps.hasAppPermission(app);
    }

    isFeatureEnabled(app: AppRoute) {
        return this.sphereApps.isFeatureEnabled(app);
    }
}
